import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import API_BASE_URL from "../config";

const HRLetterGenerator = () => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [letterType, setLetterType] = useState("");
  const [templates, setTemplates] = useState({});
  const [templateContent, setTemplateContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newTemplateName, setNewTemplateName] = useState("");
  const [letterPadFile, setLetterPadFile] = useState(null);
  const [letterPads, setLetterPads] = useState([]);
  const [selectedLetterPadId, setSelectedLetterPadId] = useState("");
  const navigate = useNavigate();

  const token = sessionStorage.getItem("token");


  // 🔐 Authentication check
  useEffect(() => {
    const token = sessionStorage.getItem("token"); // or sessionStorage
    if (!token) {
      toast.error("You must be logged in to access this page.");
      navigate("/");
    }
  }, [navigate]);

  // Load Bootstrap
  useEffect(() => {
    const bootstrapLink = document.createElement("link");
    bootstrapLink.rel = "stylesheet";
    bootstrapLink.href =
      "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css";
    document.head.appendChild(bootstrapLink);

    const iconsLink = document.createElement("link");
    iconsLink.rel = "stylesheet";
    iconsLink.href =
      "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css";
    document.head.appendChild(iconsLink);

    return () => {
      bootstrapLink.remove();
      iconsLink.remove();
    };
  }, []);

  // Fetch employees, templates, letter pads from backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [empRes, tmplRes, padRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/employees`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API_BASE_URL}/api/templates`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API_BASE_URL}/api/letterpads`, { headers: { Authorization: `Bearer ${token}` } })
        ]);

        setEmployees(empRes.data || []);
        if (empRes.data.length > 0) setSelectedEmployeeId(empRes.data[0].id.toString());

        const tmplMap = {};
        (tmplRes.data || []).forEach(t => tmplMap[t.name] = t);
        setTemplates(tmplMap);

        setLetterPads(padRes.data || []);
        if (padRes.data.length > 0) setSelectedLetterPadId(padRes.data[0].id.toString());

        setLoading(false);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch data.");
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  // Update template editor when letter type changes
  useEffect(() => {
    if (letterType && templates[letterType]) {
      setTemplateContent(templates[letterType].content || "");
    }
  }, [letterType, templates]);

  const fillTemplate = (template, employee) => {
    return template.replace(/\{\{(\w+)\}\}/g, (_, key) => {
      if (key === "date") return new Date().toLocaleDateString();
      if (key === "resignationDate") return employee.resignationDate || "";
      if (key === "relievingDate") return employee.relievingDate || "";
      if (key === "internshipDuration") return employee.internshipDuration || 3;
      return employee[key] || "";
    });
  };

  const generateLetter = async () => {
  const emp = employees.find(e => e.id.toString() === selectedEmployeeId);
  if (!emp || !letterType) return;

  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const content = fillTemplate(templateContent, emp);
  const marginLeft = 60;
  const marginTop = 150;
  const pageWidth = 595;
  const maxWidth = pageWidth - marginLeft * 2;

  const drawText = () => {
    const lines = doc.splitTextToSize(content, maxWidth);
    doc.setFont("times", "normal");
    doc.setFontSize(12);
    doc.text(lines, marginLeft, marginTop);
  };

  if (selectedLetterPadId) {
    const pad = letterPads.find(p => p.id.toString() === selectedLetterPadId);
    if (pad) {
      const img = new Image();
      // ✅ Use Spring static resource handler
      img.src = `${API_BASE_URL}/uploads/letterpads/${pad.fileName}`;
      img.crossOrigin = "anonymous";
      img.onload = () => {
        doc.addImage(img, "PNG", 0, 0, 595, 842);
        drawText();
        doc.save(`${emp.firstName}_${letterType}_letter.pdf`);
      };
      return;
    }
  }

  drawText();
  doc.save(`${emp.firstName}_${letterType}_letter.pdf`);
};

  const addNewTemplate = async () => {
  if (!newTemplateName.trim()) return;
  const key = newTemplateName.replace(/\s+/g, "");
  try {
    const res = await axios.post(
      `${API_BASE_URL}/api/templates`,
      { name: key, content: "" },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    // ✅ Store with full object including id
    setTemplates({ ...templates, [res.data.name]: res.data });
    setLetterType(res.data.name);
    setNewTemplateName("");
  } catch (err) {
    console.error(err);
    alert("Failed to add template");
  }
};

  const saveTemplate = async () => {
  if (!letterType) return;

  const tmpl = templates[letterType];
  if (!tmpl?.id) {
    alert("Template ID missing, cannot update!");
    return;
  }

  try {
    const res = await axios.put(
      `${API_BASE_URL}/api/templates/${tmpl.id}`,
      { name: tmpl.name, content: templateContent },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    // ✅ Update with latest backend data
    setTemplates({ ...templates, [res.data.name]: res.data });
    alert("Template saved successfully!");
  } catch (err) {
    console.error(err);
    alert("Failed to save template.");
  }
};


  const uploadLetterPad = async () => {
    if (!letterPadFile) return;
    const formData = new FormData();
    formData.append("file", letterPadFile);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/letterpads/upload`, formData);
      setLetterPads([...letterPads, res.data]);
      setSelectedLetterPadId(res.data.id.toString());
      setLetterPadFile(null);
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) return <div className="text-center my-5">Loading data...</div>;
  if (error) return <div className="alert alert-danger my-5">{error}</div>;

  return (
    <div className="container my-5">
      <div className="card shadow-lg border-0">
        <div className="card-header bg-primary text-white text-center fs-4">
          📝 HR Letter Generator
        </div>
        <div className="card-body">
          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <label className="form-label fw-semibold">Select Employee</label>
              <select
                className="form-select border-primary"
                value={selectedEmployeeId}
                onChange={(e) => setSelectedEmployeeId(e.target.value)}
              >
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id.toString()}>
                    {emp.firstName} {emp.lastName}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">Select Template</label>
              <select
                className="form-select border-primary"
                value={letterType}
                onChange={(e) => setLetterType(e.target.value)}
              >
                <option value="">-- Select Template --</option>
                {Object.keys(templates).map(key => (
                  <option key={key} value={key}>
                    {key.replace(/([A-Z])/g, " $1").replace(/^./, str => str.toUpperCase())}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="row g-3 mb-3">
            <div className="col-md-8">
              <label className="form-label fw-semibold">Upload Letter Pad</label>
              <input
                type="file"
                accept="image/*"
                className="form-control"
                onChange={(e) => setLetterPadFile(e.target.files[0])}
              />
            </div>
            <div className="col-md-4 d-flex align-items-end">
              <button className="btn btn-success w-100" onClick={uploadLetterPad}>
                <i className="bi bi-upload me-1"></i> Upload
              </button>
            </div>
          </div>

          <div className="mb-3 d-flex gap-2 align-items-center">
            <input
              type="text"
              className="form-control border-success"
              placeholder="New Template Name"
              value={newTemplateName}
              onChange={(e) => setNewTemplateName(e.target.value)}
            />
            <button className="btn btn-success fw-semibold" onClick={addNewTemplate}>
              <i className="bi bi-plus-circle me-1"></i> Add Template
            </button>
          </div>

          {letterType && (
            <div className="mb-4">
              <label className="form-label fw-semibold">Edit Template</label>
              <textarea
                className="form-control border-warning"
                rows={12}
                value={templateContent}
                onChange={(e) => setTemplateContent(e.target.value)}
              />
              <div className="d-flex justify-content-end mt-2">
                <button className="btn btn-warning" onClick={saveTemplate}>
                  <i className="bi bi-save me-1"></i> Save Template
                </button>
              </div>
              <small className="text-muted">
                Use placeholders like <code>{'{{firstName}}'}</code>, <code>{'{{lastName}}'}</code>, <code>{'{{designation}}'}</code>, etc.
              </small>
            </div>
          )}

          <div className="text-center">
            <button
              className="btn btn-primary btn-lg fw-semibold"
              disabled={!selectedEmployeeId || !letterType}
              onClick={generateLetter}
            >
              <i className="bi bi-file-earmark-text me-2"></i> Generate Letter
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HRLetterGenerator;
